import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { GeneralSelectionFacade } from "../../general-selection/facade/general-selection.facade";
import { Injectable, Signal } from "@angular/core";
import { combineLatest, filter, map, Observable, of } from "rxjs";
import { ICashflowAvailableData, ICashflowAvailableDataResult, ICashflowRequest } from "src/app/models/cashflow";
import { CashflowService } from "src/app/services/http/cashflow.service";
import { sharedDeduplicatingSwitchMap } from "../../../utils/deduplicatingSwitchMap";
import { catchError, distinctUntilChanged, shareReplay, startWith } from "rxjs/operators";
import { SnackBarService } from "../../../services/snackbar/snackBar.service";
import { LatestCashflowFacade } from "./latest-cashflow.facade";
import { toSignal } from "@angular/core/rxjs-interop";
import { CustomDatePipe } from "src/app/utils/custom-date.pipe";

/**
 * This faacde is responsible for managing cashflow data as at the date in the general selection state.
 * It should be used in scenarios where historic cashflow data is required.
 * 
 * See the LatestCashflowFacade for cashflow data which is the latest data as at 'today's' date,
 */
@UntilDestroy()
@Injectable()
export class CashflowFacade {
    private selectedDate: Signal<Date> = toSignal(this.generalSelectionFacade.getSelectedDate())

    private cashflowRequestModel$ = combineLatest([
        this.generalSelectionFacade.getSelectedEntityPortfolioState$,
        this.generalSelectionFacade.getSelectedDate(),
    ]).pipe(
        untilDestroyed(this),
        map(([generalState, selectedDate]) => {
            return {
                clientEntities: generalState.selectedEntities,
                date: selectedDate,
                portfolios: generalState.selectedPortfolios
            } as ICashflowRequest;
        }),
    );

    private cashflowData$: Observable<ICashflowAvailableDataResult> = this.cashflowRequestModel$.pipe(
        untilDestroyed(this),
        filter((request) => {
            return this.shouldRequestCashflow(request);
        }),
        map((data: ICashflowRequest) => {
            const dateWithoutTime = new Date(data.date.getFullYear(), data.date.getMonth(), data.date.getDate())
            return {
                ...data,
                date: dateWithoutTime,
            };
        }),
        distinctUntilChanged(),
        sharedDeduplicatingSwitchMap((request: ICashflowRequest) => {
            return this.cashflowService.getCashAvailableData(request).pipe(
                catchError((error) => {
                    this.snackBarService.showWarningSnackBar("Cashflow available data couldn't be loaded.");
                    return of({
                        cashflowAvailableData: null as ICashflowAvailableData,
                        date: null,
                        clientEntities: [],
                        portfolios: [],
                        error: true,
                        loading: false,
                    } as ICashflowAvailableDataResult);
                }),
                map((data: ICashflowAvailableData) => {
                    return {
                        cashflowAvailableData: data,
                        date: request.date,
                        clientEntities: request.clientEntities,
                        portfolios: request.portfolios,
                        error: false,
                        loading: false,
                    } as ICashflowAvailableDataResult;
                }),
                startWith({
                    cashflowAvailableData: null as ICashflowAvailableData,
                    date: null,
                    clientEntities: [],
                    portfolios: [],
                    error: false,
                    loading: true,
                } as ICashflowAvailableDataResult),
            );
        }),
        shareReplay(1),
    );

    /**
     * Represents cashflow data corresponding to the selected date in the general selection state.
     * 
     * This observable combines two sources:
     * - `cashflowData$`: cashflow data for the selected date (from the general selection state).
     * - `latestCashflowData$`: cashflow data specifically for "today's" date, updated in response to
     *    financial activity (e.g. buys, sells, applications, redemptions, withdrawals).
     *  
     *  We combine them because sometimes, the selected date (from general selection state) is "today", 
     *  which means both streams may hold data for the same date. 
     *  However, due to how the data update flows are structured, `cashflowData$` may not get
     *  automatically refreshed when real-time financial activity occurs.
     *  In contrast, `latestCashflowData$` is refreshed reactively after such events.
     * 
     * By combining both, we ensure that:
     *  - If the selected date is *not today*, we rely on `cashflowData$`.
     *  - If the selected date *is today*, and `latestCashflowData$` has a more up-to-date version, we use that.
     * 
     * This ensures that the displayed data is both current and correct for the selected date.
     * 
     * The stream only emits when at least one of the two sources contains data matching the currently selected date.
     */
    public combinedCashflowData$: Observable<ICashflowAvailableDataResult> = combineLatest([
        this.cashflowData$,
        this.latestCashflowFacade.latestCashflowData$,
    ]).pipe(
        filter(([cashflowData, latestCashflowData]: [ICashflowAvailableDataResult,ICashflowAvailableDataResult]) => {
            //If either stream emits something that matches the date in the general selection state then we want to continue.
            return this.cashflowDataDateMatchesDateInGeneralSelctionState(cashflowData, latestCashflowData);
        }),
        map(([cashflowData, latestCashflowData]) => {
            //Return the latest cashflow data if it matches the date in the general selection state, 
            //otherwise return the cashflow data.
            if (
                latestCashflowData.date &&
                this.customDatePipe.transform(latestCashflowData.date, "yyyy-MM-dd") ===
                    this.customDatePipe.transform(this.selectedDate(), "yyyy-MM-dd")
            ) {
                return latestCashflowData;
            } else {
                return cashflowData;
            }
        }),
        shareReplay(1)
    );
    
    public cashflowLoading = toSignal(this.cashflowData$.pipe(map(c => {
        return c?.loading ?? false;
    })));

    /**
     * Checks if either cashflowData or latestCashflowData is for the date in the general selection state.
     */
    private cashflowDataDateMatchesDateInGeneralSelctionState(cashflowData: ICashflowAvailableDataResult, latestCashflowData: ICashflowAvailableDataResult): boolean {
        return (cashflowData.date &&
            this.customDatePipe.transform(cashflowData.date, "yyyy-MM-dd") ===
            this.customDatePipe.transform(this.selectedDate(), "yyyy-MM-dd")) ||
        (latestCashflowData.date &&
            this.customDatePipe.transform(latestCashflowData.date, "yyyy-MM-dd") ===
            this.customDatePipe.transform(this.selectedDate(), "yyyy-MM-dd"))
    }

    constructor(
        private generalSelectionFacade: GeneralSelectionFacade,
        private cashflowService: CashflowService,
        private snackBarService: SnackBarService,
        private latestCashflowFacade: LatestCashflowFacade,
        private customDatePipe: CustomDatePipe
    ) {}

    /**
     * Determines if we should request cashflow data based on whether we have portfolio and entity data
     * and they are in sync.
     * @param request 
     * @returns 
     */
    private shouldRequestCashflow(request: ICashflowRequest) {
        if (request == null) return false;

        if ((request.portfolios?.length ?? 0) == 0 && (request?.clientEntities?.length ?? 0) == 0) return false;

        // if we have a mismatch on our entities and portfolios then don't request anything
        if (request.portfolios && request.portfolios.length > 0) {
            let entityIds = request.clientEntities.map((e) => e.EntityId);
            if (!request.portfolios.some((p) => entityIds.includes(p.entityId))) return false;
        }

        return true;
    }
}